import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, LogOut, ArrowLeft, X, BookOpen, Download, Users, Star, Eye, BarChart3 } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

const CATEGORIES = [
  { key: "genitori", label: "Genitori" },
  { key: "tate", label: "Tate ed Educatrici" },
  { key: "entrambi", label: "Genitori e Tate" },
];

const STATUSES = [
  { key: "draft", label: "Bozza", color: "#9CA3AF" },
  { key: "scheduled", label: "Programmata", color: "#F59E0B" },
  { key: "published", label: "Pubblicata", color: "#10B981" },
];

const empty = {
  title: "",
  subtitle: "",
  description: "",
  category: "entrambi",
  cover_image_url: "",
  pdf_url: "",
  cta_text: "Scarica gratuitamente",
  success_quote: "",
  publish_at: new Date().toISOString().slice(0, 16),
  status: "draft",
  featured: false,
};

export default function AdminResources() {
  const [resources, setResources] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(empty);
  const navigate = useNavigate();

  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/resources`, auth);
      setResources(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tv_admin_token");
        navigate("/admin/login");
      }
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title || "",
      subtitle: r.subtitle || "",
      description: r.description || "",
      category: r.category,
      cover_image_url: r.cover_image_url || "",
      pdf_url: r.pdf_url,
      cta_text: r.cta_text || "Scarica gratuitamente",
      success_quote: r.success_quote || "",
      publish_at: (r.publish_at || "").slice(0, 16),
      status: r.status || "draft",
      featured: !!r.featured,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, publish_at: new Date(form.publish_at).toISOString() };
      if (editing) {
        await axios.put(`${API_URL}/admin/resources/${editing.id}`, payload, auth);
        toast.success("Risorsa aggiornata");
      } else {
        await axios.post(`${API_URL}/admin/resources`, payload, auth);
        toast.success("Risorsa creata");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa risorsa? L'azione è irreversibile.")) return;
    try {
      await axios.delete(`${API_URL}/admin/resources/${id}`, auth);
      toast.success("Risorsa eliminata");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const logout = () => {
    localStorage.removeItem("tv_admin_token");
    navigate("/admin/login");
  };

  const filtered = filter === "all" ? resources : resources.filter(r => r.status === filter);
  const totalDownloads = resources.reduce((s, r) => s + (r.downloads_count || 0), 0);
  const totalForms = resources.reduce((s, r) => s + (r.form_submissions_count || 0), 0);
  const totalConsents = resources.reduce((s, r) => s + (r.consent_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="" className="h-12 w-12" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Biblioteca T.A.T.A.</p>
              <h1 className="font-display text-3xl md:text-4xl text-[#3D2140]">Gestione Risorse</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/newsletter" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="link-newsletter">
              💌 Newsletter
            </Link>
            <Link to="/admin/statistiche" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="link-stats">
              <BarChart3 size={14} /> Statistiche
            </Link>
            <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold">Risorse totali</p>
            <p className="font-display text-4xl text-[#3D2140] mt-1">{resources.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1"><Download size={12} /> Downloads</p>
            <p className="font-display text-4xl text-[#FF2768] mt-1">{totalDownloads}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1"><BookOpen size={12} /> Form compilati</p>
            <p className="font-display text-4xl text-[#3D2140] mt-1">{totalForms}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold flex items-center gap-1"><Users size={12} /> Consensi</p>
            <p className="font-display text-4xl text-[#81C3C9] mt-1">{totalConsents}</p>
          </div>
        </div>

        {/* Filter + New */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFilter("all")} className={`px-4 py-2 rounded-full text-sm font-semibold ${filter === "all" ? "bg-[#3D2140] text-white" : "bg-white border border-[#3D2140]/15 text-[#3D2140]"}`}>Tutte ({resources.length})</button>
            {STATUSES.map(s => (
              <button key={s.key} onClick={() => setFilter(s.key)} className={`px-4 py-2 rounded-full text-sm font-semibold ${filter === s.key ? "text-white" : "bg-white border border-[#3D2140]/15 text-[#3D2140]"}`} style={filter === s.key ? { background: s.color } : {}}>{s.label} ({resources.filter(r => r.status === s.key).length})</button>
            ))}
          </div>
          <button onClick={openNew} className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF2768] text-white rounded-full font-semibold hover:bg-[#E01F5A] transition-colors">
            <Plus size={16} /> Nuova risorsa
          </button>
        </div>

        {/* Resource list */}
        <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden">
          {filtered.length === 0 && <p className="p-10 text-center text-[#3D2140]/60">Nessuna risorsa in questa categoria.</p>}
          {filtered.map((r, i) => {
            const status = STATUSES.find(s => s.key === r.status) || STATUSES[0];
            const cat = CATEGORIES.find(c => c.key === r.category)?.label || r.category;
            return (
              <div key={r.id} className={`p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center ${i > 0 ? "border-t border-[#3D2140]/8" : ""}`}>
                <div className="md:col-span-6">
                  <div className="flex items-start gap-3">
                    {r.featured && <Star size={16} className="text-[#FF2768] mt-1 fill-[#FF2768] flex-shrink-0" />}
                    <div>
                      <h3 className="font-semibold text-[#3D2140] leading-snug">{r.title}</h3>
                      {r.subtitle && <p className="text-sm text-[#3D2140]/60 mt-0.5">{r.subtitle}</p>}
                      <p className="text-xs text-[#3D2140]/50 mt-1">{cat} · pubblicazione: {new Date(r.publish_at).toLocaleString("it-IT")}</p>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <span className="inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold text-white" style={{ background: status.color }}>{status.label}</span>
                </div>
                <div className="md:col-span-2 text-xs text-[#3D2140]/70">
                  <p>📥 {r.downloads_count || 0} download</p>
                  <p>✉️ {r.consent_count || 0} consensi</p>
                </div>
                <div className="md:col-span-2 flex md:justify-end gap-2">
                  <Link to={`/admin/risorse/anteprima/${r.id}`} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-[#81C3C9] hover:text-white transition-colors" title="Anteprima pubblica" data-testid={`resource-preview-${r.id}`}>
                    <Eye size={14} />
                  </Link>
                  <button onClick={() => openEdit(r)} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-[#FF2768] hover:text-white transition-colors" title="Modifica">
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => del(r.id)} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-red-500 hover:text-white transition-colors" title="Elimina">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8 bg-[#3D2140]/70 backdrop-blur-sm overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-2xl bg-white rounded-[24px] shadow-2xl my-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#3D2140]/8">
              <h2 className="font-display text-2xl text-[#3D2140]">{editing ? "Modifica Risorsa" : "Nuova Risorsa"}</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-[#3D2140]/5"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <Field label="Titolo *"><input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="input" /></Field>
              <Field label="Sottotitolo"><input value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} className="input" /></Field>
              <Field label="Descrizione breve"><textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Categoria *">
                  <select required value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input">
                    {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Stato *">
                  <select required value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input">
                    {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="URL immagine copertina">
                <input value={form.cover_image_url} onChange={e => setForm({...form, cover_image_url: e.target.value})} placeholder="https://..." className="input" />
                <p className="text-[11px] text-[#3D2140]/50 mt-1">Incolla l'URL di un'immagine da Canva, Instagram, Drive, etc.</p>
              </Field>
              <Field label="URL PDF scaricabile *">
                <input required value={form.pdf_url} onChange={e => setForm({...form, pdf_url: e.target.value})} placeholder="https://.../file.pdf" className="input" />
              </Field>
              <Field label="Testo pulsante CTA">
                <input value={form.cta_text} onChange={e => setForm({...form, cta_text: e.target.value})} className="input" />
              </Field>
              <Field label="Citazione dopo il download (opzionale)">
                <textarea rows="2" value={form.success_quote} onChange={e => setForm({...form, success_quote: e.target.value})} placeholder="Es: Osservare non significa controllare. Significa concedersi il tempo di vedere davvero." className="input" />
                <p className="text-[11px] text-[#3D2140]/50 mt-1">Se compilata, appare nella schermata di successo dopo il form.</p>
              </Field>
              <Field label="Data e ora pubblicazione *">
                <input type="datetime-local" required value={form.publish_at} onChange={e => setForm({...form, publish_at: e.target.value})} className="input" />
                <p className="text-[11px] text-[#3D2140]/50 mt-1">Se lo stato è "Programmata", la risorsa apparirà automaticamente alla data indicata.</p>
              </Field>
              <label className="flex items-center gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="h-4 w-4 accent-[#FF2768]" />
                <span className="text-sm text-[#3D2140]">⭐ Metti in evidenza questa risorsa</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-[#FF2768] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E01F5A]">
                  {editing ? "Salva modifiche" : "Crea risorsa"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 rounded-full border border-[#3D2140]/15 text-[#3D2140] font-semibold">Annulla</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;padding:0.75rem 1rem;border-radius:12px;background:#FAF8F5;border:1px solid rgba(10,43,45,0.1);color:#3D2140;font-size:0.9rem;outline:none;transition:border-color 0.2s}.input:focus{border-color:#FF2768;background:#fff}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#3D2140] mb-1.5 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
